import { useTodaysCalendarEvents } from '@renderer/hooks/use-calendar-events'
import { useCurrentMeeting } from '@renderer/hooks/useCurrentMeeting'
import { useMemo } from 'react'
import { findMatchingCalendarEvent } from '@shared/utils/calendar-matching'
import { CalendarEvent, DetectedMeeting, RecallDetectedMeeting } from '@highlight/common'

export const useMatchingCalendarEvent = (meeting: DetectedMeeting | RecallDetectedMeeting | undefined) => {
  const { data: calendarEvents } = useTodaysCalendarEvents()

  return useMemo(() => {
    if (!meeting) return null
    return findMatchingCalendarEvent({ url: meeting.url, title: meeting.windowTitle }, calendarEvents)
  }, [meeting?.url, meeting?.windowTitle, calendarEvents])
}

/**
 * The calendar event behind the recorder's current meeting, if any. After a
 * refused start that meeting stays parked, so a toast raised by the gate can
 * still send the user to the right meeting's view.
 */
export const useCurrentMeetingCalendarEvent = (): CalendarEvent | undefined => {
  const { currentDetectedMeeting, currentScheduledMeeting } = useCurrentMeeting()
  const { data: calendarEvents } = useTodaysCalendarEvents()
  const matched = useMatchingCalendarEvent(currentDetectedMeeting ?? undefined)

  return useMemo(() => {
    if (currentScheduledMeeting) {
      return calendarEvents?.find((event) => event.id === currentScheduledMeeting.id)
    }
    const linked =
      currentDetectedMeeting && 'calendarEvent' in currentDetectedMeeting
        ? currentDetectedMeeting.calendarEvent
        : undefined
    return linked ?? matched ?? undefined
  }, [currentDetectedMeeting, currentScheduledMeeting, calendarEvents, matched])
}
